import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowLeft, Shield, Star, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PERKS = [
  { icon: CheckCircle, label: "Blue tick on profile, posts and comments" },
  { icon: Star, label: "Higher rank in search and explore" },
  { icon: Lightning, label: "Faster upload + early access to new gifts" },
  { icon: Shield, label: "Anti-impersonation badge" },
];

export default function Verify() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/verification/status");
        setStatus(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const payWithWaves = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/verification/subscribe-waves");
      toast.success("You're verified! 🇯🇲");
      setStatus((s) => ({ ...(s || {}), is_verified: data.is_verified, verified_until: data.verified_until }));
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate verification");
    } finally {
      setPaying(false);
    }
  };

  const payWithPayPal = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/verification/order");
      // Redirect to PayPal — the existing wallet flow has a button SDK; for simplicity we just hand-off
      toast.info("Redirecting to PayPal…");
      // PayPal sandbox approve URL pattern
      const approveUrl = `https://www.sandbox.paypal.com/checkoutnow?token=${data.order_id}`;
      window.location.href = approveUrl;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PayPal error");
      setPaying(false);
    }
  };

  const expires = status?.verified_until ? new Date(status.verified_until).toLocaleDateString() : null;

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="verify-page">
      <div className="px-5 pt-6">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream mb-4" data-testid="verify-back">
          <ArrowLeft size={18} />
        </button>
        <div className="w-20 h-20 rounded-[35%] bg-gradient-to-br from-papaya via-jam-gold to-jam-green flex items-center justify-center papaya-glow mb-6">
          <CheckCircle size={44} weight="fill" className="text-ink" />
        </div>
        <p className="text-papaya text-[10px] tracking-[0.35em] uppercase font-black">Blue Tick</p>
        <h1 className="text-cream text-5xl font-black tracking-tighter mt-1">Get verified.</h1>
        <p className="text-cream/65 text-sm mt-3 leading-relaxed">
          $5 / month. Stand out, protect your name, and unlock priority placement across the app.
        </p>
      </div>

      {/* Status box */}
      {status?.is_verified ? (
        <div className="mx-5 mt-8 rounded-3xl bg-papaya/10 border border-papaya/40 p-5 papaya-glow" data-testid="verify-active">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} weight="fill" className="text-papaya" />
            <p className="text-papaya text-xs tracking-[0.25em] uppercase font-black">Active</p>
          </div>
          <p className="text-cream font-black text-lg mt-2">You're verified.</p>
          {expires && <p className="text-cream/60 text-xs mt-1">Renews / expires on {expires}</p>}
          <button
            onClick={payWithWaves}
            disabled={paying}
            data-testid="verify-extend"
            className="mt-4 w-full py-3 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-xs"
          >
            Extend +30 days ({status.price_waves} waves)
          </button>
        </div>
      ) : (
        <>
          <div className="mx-5 mt-8 space-y-3">
            {PERKS.map(({ icon: I, label }) => (
              <div key={label} className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                <div className="w-9 h-9 rounded-xl bg-papaya/10 text-papaya flex items-center justify-center flex-shrink-0">
                  <I size={18} weight="duotone" />
                </div>
                <p className="text-cream text-sm leading-relaxed flex-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="mx-5 mt-8 space-y-3">
            <button
              onClick={payWithPayPal}
              disabled={paying}
              data-testid="verify-paypal"
              className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors papaya-glow disabled:opacity-50"
            >
              Pay $5 with PayPal
            </button>
            <button
              onClick={payWithWaves}
              disabled={paying}
              data-testid="verify-waves"
              className="w-full py-3 rounded-full bg-white/5 border border-white/15 text-cream font-bold tracking-[0.2em] uppercase text-xs hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              Pay with {status?.price_waves || 500} Waves
            </button>
            <p className="text-cream/40 text-[10px] tracking-[0.25em] uppercase text-center mt-2">
              30-day subscription · Cancel anytime
            </p>
          </div>
        </>
      )}
    </div>
  );
}
